import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Smartphone,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Zap,
  Download,
  RotateCcw,
  Star,
  Play,
  Share2,
} from "lucide-react";

export const Route = createFileRoute("/services_/app-advertising")({
  head: () => ({
    meta: [
      {
        title:
          "Mobile App Advertising Services — KMWAdvertising · Installs & User Acquisition",
      },
      {
        name: "description",
        content:
          "Mobile App Advertising Services That Drive App Installs, User Engagement and Sustainable Growth. Google App Campaigns, Apple Search Ads & ASO by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Mobile App Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Accelerate App Growth with Data-Driven Mobile Advertising. Scale user acquisition, in-app conversions, and retention across Android & iOS.",
      },
    ],
  }),
  component: AppAdvertisingServicePage,
});

export function AppAdvertisingServicePage() {
  const whyAppAdsMatter = [
    {
      title: "High-Quality Installs",
      desc: "Reach users most likely to download and actively use your application through precise audience intent targeting.",
      icon: Download,
    },
    {
      title: "Improved User Engagement",
      desc: "Drive meaningful in-app interactions like user registrations, product purchases, feature usage, and booking appointments.",
      icon: Target,
    },
    {
      title: "Boost User Retention",
      desc: "Re-engage inactive users with personalized retargeting campaigns that encourage repeat usage and lower churn.",
      icon: RotateCcw,
    },
    {
      title: "Optimized Marketing Spend",
      desc: "Track installs, in-app events, customer lifetime value (LTV), and ROAS to continuously refine cost per install (CPI).",
      icon: BarChart3,
    },
    {
      title: "Multi-Ecosystem Scale",
      desc: "Promote your app across Google, Apple Search Ads, Meta, YouTube, OEM networks, and programmatic ad exchanges.",
      icon: Smartphone,
    },
  ];

  const appInstallChannels = [
    "Google App Campaigns for Installs (ACi)",
    "Apple Search Ads (ASA Search Tab & Results)",
    "Meta App Promotion (Facebook & Instagram)",
    "YouTube Video App Campaigns",
    "Programmatic Mobile DSP Networks",
    "OEM Device Networks (Samsung, Xiaomi, Oppo, Vivo)",
    "Mobile Display & Ad Exchanges",
  ];

  const installMetrics = [
    "Cost Per Install (CPI) Optimization",
    "Cost Per Action / Acquisition (CPA)",
    "Return on Ad Spend (ROAS)",
    "Install-to-Registration Conversion Rate",
    "Install-to-First Purchase Conversion Rate",
    "Day 1, Day 7, Day 30 Retention Tracking",
  ];

  const inAppAdFormats = [
    {
      title: "In-App Banner Ads",
      desc: "High-visibility header and footer mobile banners in popular app environments.",
      icon: Smartphone,
    },
    {
      title: "Interstitial Ads",
      desc: "Full-screen ad placements displayed during natural transition points in mobile usage.",
      icon: Layers,
    },
    {
      title: "Native Mobile Ads",
      desc: "Ad units matching the look, feel, and UI design of the host mobile application.",
      icon: Search,
    },
    {
      title: "Rewarded Video Ads",
      desc: "Interactive video ads offering users in-app rewards upon full ad view completion.",
      icon: Play,
    },
    {
      title: "Playable & Interactive Ads",
      desc: "Try-before-you-buy micro-gameplay experiences showcasing key app features.",
      icon: Sparkles,
    },
  ];

  const reEngagementObjectives = [
    "Increasing repeat monthly active usage (MAU)",
    "Recovering abandoned in-app cart purchases",
    "Driving high-margin in-app transactions",
    "Promoting new feature releases & updates",
    "Encouraging annual subscription renewals",
    "Improving overall Customer Lifetime Value (LTV)",
  ];

  const asoSupportPoints = [
    "High-intent App Store keyword research",
    "App Title & Subtitle optimization",
    "Short & Long Description copywriting",
    "High-conversion Screenshot recommendations",
    "App Preview Video creative guidance",
    "Review & Rating management strategy",
    "Primary & Secondary Category optimization",
  ];

  const mobilePlatforms = [
    "Google App Campaigns",
    "Apple Search Ads (ASA)",
    "Meta Ads (Facebook & IG)",
    "YouTube App Ads",
    "Google Display Network",
    "Programmatic Mobile DSPs",
    "Mobile Ad Exchanges",
    "OEM Device Stores",
    "Gaming Ad Networks",
    "Premium In-App Publishers",
  ];

  const industriesServed = [
    "E-commerce & Shopping Apps",
    "FinTech & Neobanking",
    "EdTech & E-Learning",
    "HealthTech & Fitness",
    "Hospitality & Food Delivery",
    "Travel & Cab Aggregators",
    "Mobile Gaming & Esports",
    "Entertainment & OTT Streaming",
    "Retail & Quick-Commerce",
    "Banking, Insurance & Finance",
    "Healthcare & Telemedicine",
    "Automobile & Mobility Apps",
  ];

  const kmwaStrengths = [
    "25+ years of advertising and media strategy heritage",
    "Certified mobile app growth & ASA specialists",
    "Cross-platform campaign management (Android & iOS)",
    "AI-powered automated bid & CPI optimization",
    "Advanced cohort segmentation & LTV tracking",
    "A/B creative testing & screenshot optimization",
    "Transparent attribution reporting (MMP integration)",
    "User retention & re-engagement campaign playbooks",
    "Data-driven decision making & fraud prevention",
    "ROI-focused acquisition management",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">App Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Smartphone className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Mobile App Advertising Services That Drive App Installs, User Engagement and Sustainable Growth
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Mobile App User Acquisition & Retention
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Accelerate App Growth with Data-Driven Mobile Advertising
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  With billions of smartphone users worldwide, mobile applications have become an essential channel for businesses to engage customers, deliver services and generate revenue. However, launching an app is only the beginning. Achieving sustained growth requires a strategic advertising approach that attracts high-quality users, increases installs, improves retention and maximises lifetime value.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  App advertising enables businesses to promote their mobile applications across search engines, social media platforms, app stores, mobile websites and in-app networks. By combining advanced targeting, creative testing and performance analytics, brands reach users who convert and retain.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we provide end-to-end mobile app advertising services designed to support every stage of the app growth journey across Android and iOS ecosystems.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Mobile App Growth Audit
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY APP ADVERTISING MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Scalable User Acquisition
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why App Advertising Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                The mobile app marketplace is highly competitive. Effective app advertising ensures your application reaches high-intent users while maximizing return on marketing spend.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyAppAdsMatter.map((item, idx) => {
                const AIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <AIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* APP INSTALL CAMPAIGN SUITES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                User Acquisition & Retention
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Mobile App Advertising Services
              </h2>
            </div>

            {/* APP INSTALL & USER ACQUISITION GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* INSTALL CAMPAIGNS */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <Download className="h-3.5 w-3.5" />
                    Scale Installs
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">App Install Campaigns</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Acquire High-Quality Users Across Android & iOS
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our app install campaigns focus on attracting users who actively engage beyond the initial download, optimizing toward down-funnel events.
                </p>

                <div className="space-y-2 pt-2">
                  {appInstallChannels.map((ac, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{ac}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* INSTALL METRICS & UA */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <BarChart3 className="h-3.5 w-3.5" />
                    Performance Optimization
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Targeting & Cost Optimization</h3>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We manage and optimize campaigns around critical unit economics to ensure scalable return on ad spend (ROAS).
                </p>

                <div className="space-y-2 pt-2">
                  {installMetrics.map((im, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{im}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* IN-APP AD FORMATS */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  In-App Advertising Formats
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Reach engaged mobile users within their favorite apps through high-performing visual ad units.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {inAppAdFormats.map((fmt, idx) => {
                  const FIcon = fmt.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <FIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{fmt.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{fmt.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RE-ENGAGEMENT & ASO SUPPORT */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Re-engagement */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-[var(--glow-purple)]" />
                  App Re-Engagement & Retargeting
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Bring inactive users back and boost retention through personalized deep-linking offers:
                </p>
                <div className="space-y-2 pt-2">
                  {reEngagementObjectives.map((ro, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{ro}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ASO */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-400" />
                  App Store Optimisation (ASO) Support
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Combine paid app campaigns with organic App Store discoverability optimization:
                </p>
                <div className="space-y-2 pt-2">
                  {asoSupportPoints.map((aso, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                      <span>{aso}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PLATFORMS & INDUSTRIES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Platforms */}
              <div className="lg:col-span-5 space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block">
                  Ecosystem Coverage
                </span>
                <h3 className="font-display text-2xl font-bold text-white">Platforms We Advertise On</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Our multi-platform approach maximizes reach across mobile ecosystems while ensuring budget efficiency.
                </p>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {mobilePlatforms.map((mp, i) => (
                    <div key={i} className="p-2.5 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium">
                      {mp}
                    </div>
                  ))}
                </div>
              </div>

              {/* Industries */}
              <div className="lg:col-span-7 space-y-4">
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block">
                  Vertical Expertise
                </span>
                <h3 className="font-display text-2xl font-bold text-white">Industries We Serve</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Customized mobile app user acquisition campaigns aligned with industry benchmarks and user retention flows.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                  {industriesServed.map((ind, i) => (
                    <div key={i} className="p-3 rounded-xl glass border border-white/10 text-xs text-white/90 font-medium flex items-center gap-2">
                      <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span>{ind}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Proven App Growth Partner
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Smartphone className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Mobile App Growth Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Accelerate Your Mobile App Installs & User Retention?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWAdvertising LLP's mobile app advertising specialists to build high-converting user acquisition campaigns across Android and iOS.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Mobile App Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
